import React, { useState, useEffect } from 'react';
import { PhotoIcon, FolderIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useProject } from '../../../context/ProjectContext';
import { useToast } from '../../../context/ToastContext';
import { useConfirm } from '../../../context/ConfirmContext';
import { supabase } from '../../../lib/supabase';
import UnsplashImagePicker from '../../editor/UnsplashImagePicker';

export default function Media() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnsplashPicker, setShowUnsplashPicker] = useState(false);
  const { currentProject } = useProject();
  const { addToast } = useToast();
  const { confirm } = useConfirm();

  useEffect(() => {
    if (currentProject?.id) {
      fetchMedia();
    }
  }, [currentProject?.id]);

  const fetchMedia = async () => {
    try {
      const { data, error } = await supabase
        .from('media')
        .select(`
          *,
          media_usage (
            content_id,
            content_type
          )
        `)
        .eq('project_id', currentProject.id)
        .eq('is_archived', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMedia(data || []);
    } catch (error) {
      console.error('Error fetching media:', error);
      addToast('Failed to fetch media', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${currentProject.id}/${fileName}`;

      const { error: uploadError, data } = await supabase.storage
        .from('content_images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('content_images')
        .getPublicUrl(filePath);

      // Save to media table
      const { error: dbError } = await supabase
        .from('media')
        .insert({
          project_id: currentProject.id,
          organization_id: currentProject.organization_id,
          url: publicUrl,
          filename: file.name,
          file_type: file.type.startsWith('image/') ? 'image' : 'video',
          mime_type: file.type,
          source: 'upload',
          source_id: filePath,
          metadata: {
            size: file.size,
            type: file.type
          }
        });

      if (dbError) throw dbError;
      
      addToast('Media uploaded successfully', 'success');
      fetchMedia();
    } catch (error) {
      console.error('Error uploading media:', error);
      addToast('Failed to upload media', 'error');
    }
  };

  const handleUnsplashSelect = async (image) => {
    try {
      const { error } = await supabase
        .from('media')
        .insert({
          project_id: currentProject.id,
          organization_id: currentProject.organization_id,
          url: image.urls.regular,
          filename: `unsplash-${image.id}`,
          file_type: 'image',
          mime_type: 'image/jpeg',
          source: 'unsplash',
          source_id: image.id,
          metadata: {
            attribution: {
              name: image.user.name,
              username: image.user.username,
              link: image.user.links.html
            },
            description: image.description,
            dimensions: {
              width: image.width,
              height: image.height
            }
          }
        });

      if (error) throw error;
      
      addToast('Stock image added successfully', 'success');
      fetchMedia();
      setShowUnsplashPicker(false);
    } catch (error) {
      console.error('Error adding stock image:', error);
      addToast('Failed to add stock image', 'error');
    }
  };

  const handleDelete = async (mediaItem) => {
    const confirmed = await confirm({
      title: 'Delete Media',
      message: 'Are you sure you want to delete this media item? This cannot be undone.',
      confirmText: 'Delete',
      confirmVariant: 'danger'
    });

    if (!confirmed) return;

    try {
      // If it's a uploaded file, delete from storage
      if (mediaItem.source === 'upload') {
        const { error: storageError } = await supabase.storage
          .from('content_images')
          .remove([mediaItem.source_id]);

        if (storageError) throw storageError;
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('media')
        .delete()
        .eq('id', mediaItem.id);

      if (dbError) throw dbError;

      addToast('Media deleted successfully', 'success');
      fetchMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      addToast('Failed to delete media', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Media Library</h1>
        
        <div className="flex gap-2">
          <label className="px-4 py-2 bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-colors cursor-pointer">
            <input
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={handleFileUpload}
            />
            Upload Media
          </label>
          
          <button
            onClick={() => setShowUnsplashPicker(true)}
            className="px-4 py-2 border border-accent-primary text-accent-primary rounded-lg hover:bg-accent-primary/10 transition-colors"
          >
            Add Stock Image
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.map((item) => (
            <div key={item.id} className="group relative">
              <div className="aspect-video rounded-lg overflow-hidden bg-dark-300">
                <img
                  src={item.url}
                  alt={item.filename}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  onClick={() => handleDelete(item)}
                  className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
                >
                  <TrashIcon className="w-5 h-5 text-white" />
                </button>
              </div>

              {item.source === 'unsplash' && item.metadata?.attribution && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1">
                  Photo by {item.metadata.attribution.name}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showUnsplashPicker && (
        <UnsplashImagePicker
          onSelect={handleUnsplashSelect}
          onClose={() => setShowUnsplashPicker(false)}
        />
      )}
    </div>
  );
} 